// 모든 품질에서 지면에 남는 부드러운 접촉 그림자. 기존 인스턴스 한 번으로 그린다.
import * as THREE from 'three/webgpu';
import { attribute, float, length, smoothstep, uv, vec3 } from 'three/tsl';
import { HERO, MOB_CAPACITY, MOB_STATS } from '../shared/config';
import { BossState, MobState, type GameView } from '../shared/types';
import { compose } from './util';

const CAP = MOB_CAPACITY + 2;
const GROUND_Y = 0.025;

export class BlobShadows {
  readonly mesh: THREE.InstancedMesh;
  private readonly opacity = new THREE.InstancedBufferAttribute(new Float32Array(CAP), 1);
  private mapped = false;

  constructor() {
    const geo = new THREE.PlaneGeometry(2, 2);
    geo.rotateX(-Math.PI / 2);
    geo.setAttribute('contactOpacity', this.opacity);
    this.opacity.setUsage(THREE.DynamicDrawUsage);
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: true });
    const radius = length(uv().sub(0.5).mul(2));
    const falloff = float(1).sub(smoothstep(0, 1, radius));
    material.colorNode = vec3(0.035, 0.04, 0.05);
    material.opacityNode = falloff.mul(falloff).mul(attribute('contactOpacity', 'float'));
    material.polygonOffset = true;
    material.polygonOffsetFactor = -1;
    material.polygonOffsetUnits = -1;
    this.mesh = new THREE.InstancedMesh(geo, material, CAP);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    this.mesh.count = 0;
  }

  /** 그림자 맵이 있으면 접촉부만 옅게, 없으면 넓은 주변 차폐까지 보완한다. */
  setShadowMapEnabled(enabled: boolean): void { this.mapped = enabled; }

  private place(index: number, x: number, z: number, radius: number, height: number, aspect = 1): void {
    const lift = Math.max(0, height);
    const size = radius * (this.mapped ? 1 : 1.2) * (1 + Math.min(lift, 2) * 0.35);
    compose(this.mesh.instanceMatrix.array as Float32Array, index * 16, x, GROUND_Y, z, 0, 0, size, 1, size * aspect);
    // 공중에 뜬 몸을 따라 원판을 올리지 않는다. 넓어지고 옅어진 그림자는 바닥에 남는다.
    this.opacity.setX(index, (this.mapped ? 0.32 : 0.48) / (1 + lift * 3));
  }

  /**
   * heroX/Z는 루트 모션·렌더 보간 뒤 현재 보이는 몸의 접지 투영 월드 좌표다.
   * 논리 Hero 위치에서 추측하지 않고 호출자가 같은 프레임의 시각 위치를 전달한다.
   * heroY는 그 위치의 지면 위 부양 높이(m)다. 그림자 자체는 현재 평면 맵의 GROUND_Y에 남는다.
   */
  update(view: GameView, heroX: number, heroY: number, heroZ: number, bossX: number, bossZ: number): void {
    let n = 0;
    this.place(n++, heroX, heroZ, HERO.radius * 1.4, heroY, 0.8);
    const m = view.mobs;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying) continue;
      const r = MOB_STATS[m.kind[i]].radius * (m.elite[i] ? 1.4 : 1) * 1.15;
      this.place(n++, m.x[i], m.z[i], r, m.y[i]);
    }
    const b = view.boss;
    if (b.state !== BossState.Inactive && b.state !== BossState.Dead) this.place(n++, bossX, bossZ, 3.2, 0);
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.opacity.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
  }
}
